import { NavLink, useLocation, useMatch, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import {
  Bell,
  Inbox,
  Kanban,
  BarChart2,
  Bot,
  Boxes,
  CalendarDays,
  LogOut,
  Users,
  MessageCircle,
  Building2,
  ShoppingCart,
  ClipboardList,
  UserCog,
  FileCode,
  FileText,
  DollarSign,
  CreditCard,
  Tag,
  Package,
  ChevronDown,
  Send,
  Globe,
  X,
  Instagram,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { brandByKey } from "@school-sales-ai/shared";
import { brandMarkSrc } from "@/lib/brand";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import { useBrandDocumentHead } from "@/hooks/useBrandDocumentHead";
import { useClientBranding } from "@/hooks/useClientBranding";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { useInboxUnread } from "@/hooks/useInbox";
import { listClientsApi, listMyClientsApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { StatusBar } from "./StatusBar.js";
import { BottomNav } from "./BottomNav.js";
import { Wordmark } from "./Wordmark.js";
import { TopBar } from "./notifications/TopBar.js";
import { ClientScopedOutlet } from "./ClientScopedOutlet.js";
import { FEATURE_KEYS } from "@school-sales-ai/shared";

const NAV_ITEMS = [
  // First: it is the "what needs me right now" queue, and the only surface the
  // team has for incoming WhatsApp since the phone was lost.
  { to: "/buzon", label: "Buzón", icon: Inbox, end: false },
  // One entry: the board and the table are two modes of the same page.
  { to: "/pipeline", label: "Prospectos", icon: Kanban, end: false },
  // Below Prospectos: an order comes out of a conversation, and this is the
  // queue of the ones somebody still has to deliver.
  { to: "/pedidos", label: "Pedidos", icon: ShoppingCart, end: false },
  // Pedidos' sibling for a tenant that closes by quote; a plan has one or the
  // other (`FEATURE_CONFLICTS`), so the two never show together.
  { to: "/cotizaciones", label: "Cotizaciones", icon: ClipboardList, end: false },
  // Directly below Prospectos: that is where an envío is launched from, and
  // this is where its result is read.
  { to: "/envios", label: "Envíos masivos", icon: Send, end: false },
  { to: "/visitas", label: "Visitas", icon: CalendarDays, end: false },
  { to: "/reports", label: "Reportes", icon: BarChart2, end: false },
  { to: "/catalogo", label: "Catálogo", icon: Boxes, end: false },
  { to: "/agent", label: "Agente", icon: Bot, end: false },
] as const;

// On a lead detail page the route matches no nav item, so we keep the nav item
// the user came FROM highlighted. A lead can now be opened from the Buzón as
// well as the pipeline, and hardcoding "/pipeline" would light up Prospectos
// while the user is reading something they opened from the Buzón. The origin
// rides in router state — the same value resolveBackTarget reads in LeadDetail.
const DEFAULT_DETAIL_ORIGIN = "/pipeline";
const DETAIL_ORIGINS = ["/buzon", "/pipeline"];

function resolveDetailOrigin(state: unknown): string {
  if (typeof state === "object" && state !== null && "from" in state) {
    const from = state.from;
    if (typeof from === "string" && DETAIL_ORIGINS.includes(from)) return from;
  }
  return DEFAULT_DETAIL_ORIGIN;
}

const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
  cn(
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
    isActive
      ? "bg-lime text-moss"
      : "text-ink-300 hover:bg-ink-700 hover:text-white",
  );

// A school entry in the switcher — the common shape of both the super-admin
// (all clients) and account-manager (assigned) client lists.
type SwitcherClient = { id: string; name: string };

function ClientSwitcher({
  managedOnly,
}: {
  managedOnly: boolean;
}): React.JSX.Element {
  const { activeClientId, setActiveClient, clearActiveClient } =
    useClientSwitcherStore();

  // Account managers only see their assigned schools; super-admins see all.
  const { data: clients = [] } = useQuery<SwitcherClient[]>({
    queryKey: managedOnly ? ["my-clients"] : ["super-admin-clients"],
    queryFn: managedOnly ? listMyClientsApi : listClientsApi,
  });

  return (
    <div className="mx-2 mb-2">
      <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-widest text-[oklch(1_0_0_/_0.28)]">
        Cliente activo
      </p>
      <div className="relative">
        <select
          value={activeClientId ?? ""}
          onChange={(e) => {
            const id = e.target.value;
            if (!id) { clearActiveClient(); return; }
            const client = clients.find((c) => c.id === id);
            if (client) setActiveClient(client.id, client.name);
          }}
          className="w-full appearance-none rounded-md bg-ink-700 px-3 py-2 pr-8 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lime-dk"
        >
          <option value="">— Ninguno —</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-ink-400" />
      </div>
      {activeClientId && (
        <button
          onClick={clearActiveClient}
          className="mt-1 flex items-center gap-1 px-1 text-[11px] text-ink-400 hover:text-white"
        >
          <X size={11} />
          Quitar selección
        </button>
      )}
    </div>
  );
}

export function Layout(): React.JSX.Element {
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const setSession = useAuthStore((s) => s.setSession);
  const profile = useCurrentUserProfile();
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);
  const activeClientName = useClientSwitcherStore((s) => s.activeClientName);
  const location = useLocation();
  const onLeadDetail = useMatch("/leads/:id") !== null;
  // Which nav item to keep highlighted while on a lead detail (null elsewhere).
  const detailOriginTo = onLeadDetail ? resolveDetailOrigin(location.state) : null;

  const isSuperAdmin = profile?.role === "super_admin";
  const isAdmin = profile?.role === "admin";
  const isAccountManager = profile?.role === "account_manager";
  // Both super-admins and account managers pick a school via the client switcher
  // before any client-scoped nav appears; the difference is which schools they
  // can pick (all vs. their assigned set) and that AMs get no Platform section.
  const usesClientSwitcher = isSuperAdmin || isAccountManager;
  // The school label under the wordmark. Switcher roles (super-admin, account
  // manager) belong to no client, so it follows their current selection;
  // everyone else is scoped to their own school and it comes from their
  // profile. It used to be the pilot school's name, hardcoded.
  const workspaceName = usesClientSwitcher
    ? (activeClientName ?? "Sin selección")
    : (profile?.clientName ?? "");
  // Client-scoped nav is visible to admins and members always, and to switcher
  // roles only once a school is selected.
  const showClientNav = isAdmin || profile?.role === "member" || (usesClientSwitcher && !!activeClientId);
  const showAdminSection = isAdmin || (usesClientSwitcher && !!activeClientId);
  // The badge is what makes the Buzón useful from any other page — without it
  // the inbox only tells you something arrived once you are already looking at
  // it. Gated on the same condition as the client-scoped nav: a switcher role
  // with no school selected has no client scope and the endpoint would 403.
  const { data: inboxCounts } = useInboxUnread(showClientNav);
  // One badge, two meanings, and the colour is what disambiguates the number: red
  // counts prospects waiting for a person, lime counts unread conversations. The
  // handoff count wins when there is one because it is the more urgent fact AND
  // because it is the thing nothing else announces any more — with WABA the team
  // stopped being able to receive the WhatsApp handoff alert. Showing `unreadTotal`
  // here would hide a handoff somebody already opened and never answered, which is
  // precisely the lead that gets lost.
  const navBadge =
    inboxCounts && inboxCounts.handoffTotal > 0
      ? { count: inboxCounts.handoffTotal, className: "bg-red-500 text-white" }
      : inboxCounts && inboxCounts.unreadTotal > 0
        ? { count: inboxCounts.unreadTotal, className: "bg-lime text-moss" }
        : null;

  // Per-client feature gating: the Visitas module is off unless the active
  // client's plan grants "visits". Other nav items are always available.
  const { data: features } = useClientFeatures();
  const { data: branding } = useClientBranding();
  const clientLogoUrl = branding?.logoUrl ?? null;
  // Until the query resolves this is the platform brand — a first-paint flash
  // of the default wordmark, which is the correct thing to show when we do not
  // yet know whose CRM this is.
  const brand = brandByKey(branding?.brand);
  useBrandDocumentHead(branding ? brand : null);
  const canVisits = (features ?? []).includes(FEATURE_KEYS.visits);
  // Plantillas de WhatsApp is sold per plan too — same gate, same shape.
  const canTemplates = (features ?? []).includes(FEATURE_KEYS.whatsappTemplates);
  const canOrders = (features ?? []).includes(FEATURE_KEYS.orders);
  const canQuotes = (features ?? []).includes(FEATURE_KEYS.quotes);
  // Promociones is admin-only, so it lives in the admin section rather than in
  // NAV_ITEMS — the Plantillas shape.
  const canPromotions = (features ?? []).includes(FEATURE_KEYS.promotions);
  // Notificaciones is configured by the school's admin, so it is admin-only too.
  const canNotifications = (features ?? []).includes(FEATURE_KEYS.webNotifications);
  // «Sitio web» is the tenant's public face: its admin edits and publishes it.
  const canWebsite = (features ?? []).includes(FEATURE_KEYS.website);
  // Instagram is a channel the admin connects, beside WhatsApp.
  const canInstagram = (features ?? []).includes(FEATURE_KEYS.instagram);
  const navItems = NAV_ITEMS.filter(
    (item) =>
      (item.to !== "/visitas" || canVisits) &&
      // Envíos masivos rides on the Plantillas feature — same plan gate, same
      // module. The authorization switch decides who can SEND, not who can look.
      (item.to !== "/envios" || canTemplates) &&
      (item.to !== "/pedidos" || canOrders) &&
      (item.to !== "/cotizaciones" || canQuotes),
  );

  async function handleLogout(): Promise<void> {
    await supabase.auth.signOut();
    setSession(null);
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-surface md:flex-row">
      <StatusBar />
      {/* Sidebar */}
      <aside className="hidden md:flex w-[220px] flex-shrink-0 flex-col bg-ink-900">
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-5 py-5">
          {/* The school's own mark when its plan includes Imagen institucional,
              otherwise the brand's. Only this square swaps for the SCHOOL — the
              wordmark beside it swaps for the BRAND, and the school name below
              stays either way. */}
          <img
            src={clientLogoUrl ?? brandMarkSrc(brand)}
            alt=""
            className={cn(
              "h-9 w-9 flex-shrink-0 rounded-[10px] ring-1 ring-[oklch(1_0_0_/_0.1)]",
              // Uploaded logos are arbitrary aspect ratios on unknown grounds;
              // letterbox them on white rather than cropping a wordmark.
              clientLogoUrl && "bg-white object-contain",
            )}
          />
          <div className="min-w-0">
            <Wordmark brand={brand} ground="dark" showSuffix={false} className="text-base leading-none" />
            <div className="mt-1 truncate font-mono text-[10px] uppercase tracking-wider text-ink-500">
              {workspaceName}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2">
          {/* Client switcher — super-admins (all schools) and account managers (assigned) */}
          {usesClientSwitcher && <ClientSwitcher managedOnly={isAccountManager} />}

          {/* Client-scoped nav — hidden for switcher roles without a selected school */}
          {showClientNav && (
            <>
              {usesClientSwitcher && <div className="my-1 border-t border-[oklch(1_0_0_/_0.10)]" />}
              {navItems.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    navLinkClass({ isActive: isActive || to === detailOriginTo })
                  }
                >
                  <Icon size={16} />
                  {label}
                  {to === "/buzon" && navBadge && (
                    <span
                      className={cn(
                        "ml-auto rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none",
                        navBadge.className,
                      )}
                    >
                      {navBadge.count}
                    </span>
                  )}
                </NavLink>
              ))}
            </>
          )}

          {/* Admin section — regular admins always; super-admins only when a client is selected */}
          {showAdminSection && (
            <>
              <div className="my-2 border-t border-[oklch(1_0_0_/_0.10)]" />
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-[oklch(1_0_0_/_0.28)]">
                Admin
              </p>
              <NavLink to="/users" className={navLinkClass}>
                <Users size={16} />
                Usuarios
              </NavLink>
              <NavLink to="/whatsapp-connect" className={navLinkClass}>
                <MessageCircle size={16} />
                WhatsApp
              </NavLink>
              {canInstagram && (
                <NavLink to="/instagram-connect" className={navLinkClass}>
                  <Instagram size={16} />
                  Instagram
                </NavLink>
              )}
              {canTemplates && (
                <NavLink to="/plantillas" className={navLinkClass}>
                  <FileText size={16} />
                  Plantillas
                </NavLink>
              )}
              {canPromotions && (
                <NavLink to="/promociones" className={navLinkClass}>
                  <Tag size={16} />
                  Promociones
                </NavLink>
              )}
              {canNotifications && (
                <NavLink to="/notificaciones" className={navLinkClass}>
                  <Bell size={16} />
                  Notificaciones
                </NavLink>
              )}
              {canWebsite && (
                <NavLink to="/sitio-web" className={navLinkClass}>
                  <Globe size={16} />
                  Sitio web
                </NavLink>
              )}
              <NavLink to="/facturacion" className={navLinkClass}>
                <CreditCard size={16} />
                Facturación
              </NavLink>
            </>
          )}

          {/* Platform section — super-admins get the full set; account managers get
              only Clientes (their portfolio of assigned schools), same placement. */}
          {(isSuperAdmin || isAccountManager) && (
            <>
              <div className="my-2 border-t border-[oklch(1_0_0_/_0.10)]" />
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-[oklch(1_0_0_/_0.28)]">
                Plataforma
              </p>
              <NavLink
                to={isAccountManager ? "/clients" : "/super-admin/clients"}
                className={navLinkClass}
              >
                <Building2 size={16} />
                Clientes
              </NavLink>
              {isSuperAdmin && (
                <>
                  <NavLink to="/super-admin/plans" className={navLinkClass}>
                    <Package size={16} />
                    Planes
                  </NavLink>
                  <NavLink to="/super-admin/platform-users" className={navLinkClass}>
                    <UserCog size={16} />
                    Equipo
                  </NavLink>
                  <NavLink to="/super-admin/scripts" className={navLinkClass}>
                    <FileCode size={16} />
                    Scripts
                  </NavLink>
                  <NavLink to="/super-admin/usage" className={navLinkClass}>
                    <DollarSign size={16} />
                    Uso LLM
                  </NavLink>
                  <NavLink to="/super-admin/billing" className={navLinkClass}>
                    <CreditCard size={16} />
                    Facturación
                  </NavLink>
                </>
              )}
            </>
          )}
        </nav>

        {/* User + Logout */}
        <div className="border-t border-ink-700 px-4 py-4">
          <p className="truncate text-xs font-medium text-white">
            {profile?.name ?? session?.user.email ?? ""}
          </p>
          <p className="text-[10px] text-ink-500">
            {profile?.role === "super_admin"
              ? "Super Admin"
              : profile?.role === "account_manager"
                ? "Account Manager"
                : profile?.role === "admin"
                  ? "Admin"
                  : "Miembro"}{" "}
            · Admisiones
          </p>
          <button
            onClick={handleLogout}
            className="mt-2 flex items-center gap-2 text-xs text-ink-500 transition-colors hover:text-white"
          >
            <LogOut size={13} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex flex-1 flex-col overflow-hidden">
        <TopBar />
        <ClientScopedOutlet />
      </main>

      <BottomNav />

      <Toaster richColors position="bottom-right" />
    </div>
  );
}
